/**
 * Threedium OAuth Callback Page
 * 
 * This page is the redirect target after a user authenticates with Threedium.
 * Threedium redirects here with app_user_id as a URL parameter.
 * We extract it and exchange it for our JWT tokens via the backend.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatErrorMessage } from '../../utils/errorUtils';

export function ThreediumCallbackPage() {
    const { handleThreediumCallback } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const processCallback = async () => {
            const appUserId = searchParams.get('user_app_id');
            const otp = searchParams.get('otp') || null;

            if (!appUserId) {
                setError(`Missing user_app_id from Threedium. Received params: ${JSON.stringify(Object.fromEntries(searchParams.entries()))}`);
                setProcessing(false);
                return;
            }

            try {
                const result = await handleThreediumCallback(appUserId, otp);
                if (result?.success) {
                    navigate('/dashboard', { replace: true });
                } else {
                    setError(result?.error || 'Authentication failed. Please try again.');
                    setProcessing(false);
                }
            } catch (err) {
                setError(err.message || 'An unexpected error occurred during authentication.');
                setProcessing(false);
            }
        };

        processCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
        return (
            <div className="auth-page-new" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
                    <div style={{ fontSize: '48px', marginBottom: '1rem' }}>⚠️</div>
                    <h2 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Authentication Failed</h2>
                    <p style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>{formatErrorMessage(error)}</p>
                    <button
                        onClick={() => navigate('/login', { replace: true })}
                        style={{
                            padding: '10px 24px',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'var(--accent-primary, #6366f1)',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page-new" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
            <div style={{ textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
                <p style={{ color: 'var(--text-secondary)' }}>Authenticating with Threedium...</p>
            </div>
        </div>
    );
}

export default ThreediumCallbackPage;

